import { Box, Stack, Typography } from '@mui/material';
import { FC, memo } from 'react';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { ShiftCell } from 'src/pages/TruckShifts/truckShiftGrid';

type Props = {
  cells: ShiftCell[];
};

// カスタム（便なし）勤務の背景は灰色（Lovable 一致）。
const CUSTOM_BG = '#eceff1';

// セル内の文字色。Lovable の patternText に合わせ、パターン色を暗くした色（可読性確保）。
const patternText = (color: string) => `color-mix(in srgb, ${color} 75%, black 25%)`;
// セル背景（パターン色を淡く。便なしは灰）。
const patternBg = (color: string | null) => (color ? `color-mix(in srgb, ${color} 14%, #ffffff)` : CUSTOM_BG);

// セルの時刻ラベル（多日跨ぎは 出庫/出先/帰庫、単日は開始-終了）。
const timeLabel = (cell: ShiftCell): string => {
  const start = datetimeDecorator.toHourMinutes(new Date(cell.shift.startAt));
  const end = datetimeDecorator.toHourMinutes(new Date(cell.shift.endAt));
  switch (cell.segment) {
    case 'start':
      return `出庫 ${start}-`;
    case 'middle':
      return '出先 終日';
    case 'end':
      return `→${end} 帰庫`;
    default:
      return `${start}-${end}`;
  }
};

const MAX_COMPACT_ROWS = 3;

export const BodyCellPresenter: FC<Props> = memo(({ cells }) => {
  if (cells.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', minHeight: 52, py: 1 }}>
        <Typography variant="caption" color="text.secondary">＋ 追加</Typography>
      </Box>
    );
  }

  // 1日に複数便がある場合は、色ドット＋時刻＋ドライバー姓のコンパクト行で表示（Lovable 一致）。
  // 先頭 MAX_COMPACT_ROWS 件までを行表示し、超過分は「+N件」で打ち切る。
  if (cells.length > 1) {
    const overflow = cells.length - MAX_COMPACT_ROWS;
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.15,
        px: 0.5,
        py: 0.5,
        width: '100%',
        height: '100%',
        minHeight: 52,
        backgroundColor: patternBg(cells[0].shift.shiftPattern?.color ?? null),
      }}
      >
        {cells.slice(0, MAX_COMPACT_ROWS).map((cell) => {
          const color = cell.shift.shiftPattern?.color ?? null;
          return (
            <Stack key={`${cell.shift.id}-${cell.segment}`} direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color ?? '#90a4ae', flexShrink: 0 }} />
              <Typography
                variant="caption"
                component="span"
                sx={{ fontSize: 10, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, color: color ? patternText(color) : 'text.secondary' }}
                noWrap
              >
                {timeLabel(cell)}
              </Typography>
              {cell.shift.driver && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}
                  style={cell.shift.driver.active === false ? { textDecoration: 'line-through' } : undefined}
                  noWrap
                >
                  {cell.shift.driver.name.split(' ')[0]}
                </Typography>
              )}
            </Stack>
          );
        })}
        {overflow > 0 && (
          <Typography variant="caption" component="div" sx={{ fontSize: 9, fontWeight: 600, color: 'text.secondary', textAlign: 'right', lineHeight: 1.2 }}>
            +{overflow}件
          </Typography>
        )}
      </Box>
    );
  }

  // 1便: セル内部を満たす全面塗りの単一ブロック（中央寄せ・便名 → 時刻 → ドライバー）。
  const cell = cells[0];
  const { shift, isContinuation, spanEdge } = cell;
  const patternColor = shift.shiftPattern?.color ?? null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 52 }}>
      <Box
        sx={{
          flex: 1,
          px: 0.75,
          py: 0.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          backgroundColor: patternBg(patternColor),
          position: 'relative',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ fontSize: 13, fontWeight: 700, color: patternColor ? patternText(patternColor) : 'text.secondary', lineHeight: 1.15 }}
        >
          {isContinuation ? '継続 ' : ''}
          {shift.shiftPattern?.name ?? 'カスタム'}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: patternColor ? patternText(patternColor) : 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.15,
          }}
        >
          {timeLabel(cell)}
        </Typography>
        {shift.driver && (
          <Typography
            variant="caption"
            component="div"
            sx={{ fontSize: 11, fontWeight: 500, color: 'text.primary', lineHeight: 1.15 }}
            style={shift.driver.active === false ? { textDecoration: 'line-through' } : undefined}
          >
            {shift.driver.name}
          </Typography>
        )}
        {spanEdge && (
          <Box
            data-testid="span-underline"
            sx={{
              position: 'absolute',
              bottom: 0,
              height: 3,
              opacity: 0.7,
              backgroundColor: patternColor ?? '#90a4ae',
              left: spanEdge === 'start' ? '35%' : 0,
              right: spanEdge === 'end' ? '35%' : 0,
            }}
          />
        )}
      </Box>
    </Box>
  );
});
